import { useContext, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import useWhatsApps from "../../hooks/useWhatsApps";
import { AuthContext } from "../../context/Auth/AuthContext";

// Aceitar um atendimento perguntando para onde ele vai.
//
// Antes o botão "Aceitar" mandava o `PUT` direto: a conversa entrava na mão de
// quem clicou mantendo a fila e a conexão que tivesse — e a maioria das que
// estão aguardando não tem fila nenhuma, porque ninguém as encaminhou ainda.
// O atendimento seguia sem setor, sumia de quem filtra por fila e não entrava
// em relatório de fila nenhum.
//
// O modal abre já preenchido com o que o atendimento tem hoje: quando está
// certo, é um Enter; quando está vazio, é o momento certo de dizer o setor —
// a pessoa está justamente decidindo assumir aquela conversa.
const AceitarTicketModal = ({ aberto, aoFechar, ticket, aoAceitar }) => {
	const { user } = useContext(AuthContext);
	const { findAll: listarFilas } = useQueues();
	const { whatsApps, loadingWhatsapps } = useWhatsApps();

	const [filas, setFilas] = useState([]);
	const [fila, setFila] = useState("");
	const [conexao, setConexao] = useState("");
	const [salvando, setSalvando] = useState(false);

	useEffect(() => {
		if (!aberto) return;

		// Reabrir o modal tem de refletir o atendimento atual: guardar a escolha
		// anterior faria a segunda conversa herdar o setor da primeira.
		//
		// Sem fila no atendimento, vale a do próprio atendente: quem está numa
		// fila só vai mandar a conversa para ela em 100% dos casos, e obrigar a
		// escolher toda vez é um clique cobrado sem motivo. Com mais de uma, a
		// escolha continua sendo dele — não dá para adivinhar qual.
		const filaDoUsuario =
			user?.queues?.length === 1 ? String(user.queues[0].id) : "";

		setFila(ticket?.queueId ? String(ticket.queueId) : filaDoUsuario);
		setConexao(ticket?.whatsappId ? String(ticket.whatsappId) : "");
	}, [aberto, ticket?.queueId, ticket?.whatsappId, user]);

	useEffect(() => {
		if (!aberto) return undefined;

		let vivo = true;

		// As filas do atendente primeiro. Ele quase sempre manda para uma delas,
		// e numa empresa com vinte setores a dele ficava perdida no meio.
		const minhas = new Set((user?.queues || []).map((f) => f.id));

		listarFilas()
			.then((lista) => {
				if (!vivo) return;

				const todas = Array.isArray(lista) ? lista : [];

				setFilas([
					...todas.filter((f) => minhas.has(f.id)),
					...todas.filter((f) => !minhas.has(f.id)),
				]);
			})
			.catch(() => {
				if (vivo) setFilas([]);
			});

		return () => {
			vivo = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [aberto]);

	const confirmar = async () => {
		setSalvando(true);

		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: "open",
				userId: user?.id,
				// Só manda o que foi escolhido. Enviar `null` numa fila que a
				// pessoa não mexeu apagaria o setor que o atendimento já tinha.
				...(fila ? { queueId: Number(fila) } : {}),
				...(conexao ? { whatsappId: Number(conexao) } : {}),
			});

			aoFechar();
			if (aoAceitar) aoAceitar();
		} catch (err) {
			toastError(err);
		} finally {
			setSalvando(false);
		}
	};

	const conexoes = (whatsApps || []).filter((w) => !w.isDeleted);

	return (
		<Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Aceitar atendimento</DialogTitle>
					<DialogDescription>
						{ticket?.contact?.name
							? `A conversa com ${ticket.contact.name} passa para você.`
							: "A conversa passa para você."}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-1">
					<div className="space-y-1.5">
						<Label htmlFor="fila-do-aceite">Fila</Label>
						<Select value={fila} onValueChange={setFila}>
							<SelectTrigger id="fila-do-aceite">
								<SelectValue placeholder="Escolha o setor" />
							</SelectTrigger>
							<SelectContent>
								{filas.map((f) => (
									<SelectItem key={f.id} value={String(f.id)}>
										<span className="flex items-center gap-2">
											<span
												className="h-2.5 w-2.5 shrink-0 rounded-full"
												style={{ backgroundColor: f.color }}
											/>
											{f.name}
										</span>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{!ticket?.queueId && (
							<p className="text-xs text-muted-foreground">
								Esta conversa ainda não tem setor. Sem um, ela não aparece para
								quem filtra por fila nem entra nos relatórios por fila.
							</p>
						)}
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="conexao-do-aceite">Conexão</Label>
						<Select
							value={conexao}
							onValueChange={setConexao}
							disabled={loadingWhatsapps}
						>
							<SelectTrigger id="conexao-do-aceite">
								<SelectValue placeholder="Escolha a conexão" />
							</SelectTrigger>
							<SelectContent>
								{conexoes.map((w) => (
									<SelectItem key={w.id} value={String(w.id)}>
										{w.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<p className="text-xs text-muted-foreground">
							É por ela que a resposta sai. Trocar muda o número que o cliente
							vê.
						</p>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={aoFechar} disabled={salvando}>
						Cancelar
					</Button>
					<Button onClick={confirmar} disabled={salvando}>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						Aceitar
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default AceitarTicketModal;
